import { t } from '@lingui/macro';

export interface SiteAlarmsMicrologModel {
  combos: SiteAlarmsMicrologCombosModel;
}

export interface SiteAlarmsMicrologCombosModel {
  siteId: number;
  availableChannels: AvailableChannelsModel[];
  alarmTriggerCombos: AlarmComboModel[];
}

export interface AvailableChannelsModel {
  channelNumber: number;
  channelDescription: string;
  isFlow: boolean;
  supportsTransientLogging?: boolean;
  supportsSoundFileRecording?: boolean;
}

export interface AlarmComboModel {
  combinationNumber: number;
  join: JoinType;
  enabled: boolean;
  triggers: AlarmComboTriggerModel[];
  actions: AlarmComboActionModel[];
}

export interface AlarmComboTriggerModel {
  channelNumber: number;
  checkMode: AlarmTriggerCheckModeType;
  condition: AlarmTriggerConditionType;
  conditionX: number;
  conditionY: number;
  persistence: AlarmTriggerPersistenceType;
  persistenceX: number;
  persistenceY: number;
  hysteresis: number;
}

export interface AlarmComboActionModel {
  action: AlarmActionType;
  actionValueX: number;
  actionValueY: number;
  alarmOnActivating: boolean;
  alarmOnClearing: boolean;
  neverAlarm: boolean;
  alarmCleared: boolean;
  reportTamperAlarm: boolean;
}

export enum JoinType {
  None = 'None',
  And = 'And',
  Or = 'Or',
}

export const JoinTypeOptionsFn = (): Record<JoinType, string> => ({
  [JoinType.None]: t`None`,
  [JoinType.And]: t`AND`,
  [JoinType.Or]: t`OR`,
});

export enum AlarmTriggerCheckModeType {
  LogValues = 'LogValues',
  SampleValues = 'SampleValues',
  TransientValues = 'TransientValues',
}

export const AlarmTriggerCheckModeOptionsFn = (): Record<AlarmTriggerCheckModeType, string> => ({
  [AlarmTriggerCheckModeType.LogValues]: t`Log Values`,
  [AlarmTriggerCheckModeType.SampleValues]: t`Sample Values`,
  [AlarmTriggerCheckModeType.TransientValues]: t`Transient Values`,
});

export enum AlarmTriggerConditionType {
  AboveX = 'AboveX',
  BelowX = 'BelowX',
  BetweenXAndY = 'BetweenXAndY',
  OutsideXAndY = 'OutsideXAndY',
  ChangesFasterThanX = 'ChangesFasterThanX',
  ChangesSlowerThanX = 'ChangesSlowerThanX',
  BreachesProfileX = 'BreachesProfileX',
  ConsumesMoreThanX = 'ConsumesMoreThanX',
  ConsumesLessThanX = 'ConsumesLessThanX',
  GreaterThanChannelYByMoreThanX = 'GreaterThanChannelYByMoreThanX',
  LessThanChannelYByMoreThanX = 'LessThanChannelYByMoreThanX',
}

export const AlarmTriggerConditionOptionsFn = (): Record<AlarmTriggerConditionType, string> => ({
  [AlarmTriggerConditionType.AboveX]: t`Above 'A'`,
  [AlarmTriggerConditionType.BelowX]: t`Below 'A'`,
  [AlarmTriggerConditionType.BetweenXAndY]: t`Between 'A' and 'B'`,
  [AlarmTriggerConditionType.OutsideXAndY]: t`Outside 'A' and 'B'`,
  [AlarmTriggerConditionType.ChangesFasterThanX]: t`Changes faster than 'A'`,
  [AlarmTriggerConditionType.ChangesSlowerThanX]: t`Changes slower than 'A'`,
  [AlarmTriggerConditionType.BreachesProfileX]: t`Breaches profile 'A'`,
  [AlarmTriggerConditionType.ConsumesMoreThanX]: t`Consumes more than 'A'`,
  [AlarmTriggerConditionType.ConsumesLessThanX]: t`Consumes less than 'A'`,
  [AlarmTriggerConditionType.GreaterThanChannelYByMoreThanX]: t`Greater than channel 'A' by more than 'B'`,
  [AlarmTriggerConditionType.LessThanChannelYByMoreThanX]: t`Less than channel 'A' by more than 'B'`,
});

export enum AlarmTriggerPersistenceType {
  ForXOfLastYSamples = 'ForXOfLastYSamples',
  ForGreaterThanXSeconds = 'ForGreaterThanXSeconds',
  BetweenTimesXAndY = 'BetweenTimesXAndY',
  InA24HourPeriodStartingAtX = 'InA24HourPeriodStartingAtX',
  InA1HourPeriod = 'InA1HourPeriod',
}

export const AlarmTriggerPersistenceOptionsFn = (): Record<AlarmTriggerPersistenceType, string> => ({
  [AlarmTriggerPersistenceType.ForXOfLastYSamples]: t`For ≥ 'C' of last 'D' samples`,
  [AlarmTriggerPersistenceType.ForGreaterThanXSeconds]: t`For greater than 'C' seconds`,
  [AlarmTriggerPersistenceType.BetweenTimesXAndY]: t`Between times 'C' and 'D'`,
  [AlarmTriggerPersistenceType.InA24HourPeriodStartingAtX]: t`In the 24 hours from 'C'`,
  [AlarmTriggerPersistenceType.InA1HourPeriod]: t`In a 1 hour period`,
});

export enum AlarmActionType {
  LogThisEvent = 'LogThisEvent',
  EnableFastCallRate = 'EnableFastCallRate',
  TurnOutputXOnForYSeconds = 'TurnOutputXOnForYSeconds',
  TurnOutputXOffForYSeconds = 'TurnOutputXOffForYSeconds',
  GenerateAnAlarmWithXSettings = 'GenerateAnAlarmWithXSettings',
  LogChannelXAtItsSamplePeriod = 'LogChannelXAtItsSamplePeriod',
  SyncdSoundFile = 'SyncdSoundFile',
  GenerateTransient = 'GenerateTransient',
}

export const AlarmActionTypeOptionsFn = (): Record<AlarmActionType, string> => ({
  [AlarmActionType.LogThisEvent]: t`Log this event`,
  [AlarmActionType.EnableFastCallRate]: t`Call-in at fast call rate`,
  [AlarmActionType.TurnOutputXOnForYSeconds]: t`Turn output 'X' on for 'Y' seconds`,
  [AlarmActionType.TurnOutputXOffForYSeconds]: t`Turn output 'X' off for 'Y' seconds`,
  [AlarmActionType.GenerateAnAlarmWithXSettings]: t`Generate an alarm`,
  [AlarmActionType.LogChannelXAtItsSamplePeriod]: t`Log Channel 'X' at its sample period`,
  [AlarmActionType.SyncdSoundFile]: t`Syncd sound file`,
  [AlarmActionType.GenerateTransient]: t`Generate transient`,
});
